<script lang="ts" setup>
import type { IntelligenceCompany } from '~/composables/api/intelligence/companies/useFetchIntelligenceCompanies'

export interface BillingDetails {
  country: App.Internals.Enums.Country
  companyName: string
  companyIdent?: string
  firstName: string
  lastName: string
  address?: string
  postNumber?: string
  city?: string
  email?: string
  phone?: string
}

defineProps<{
  disabled?: boolean
  required?: Partial<Record<Exclude<keyof BillingDetails, 'country'>, boolean>>
}>()

const billingDetails = defineModel<BillingDetails>({ required: true, default: {} })

const { t } = useI18n()
const { translateForCountry } = useCountryAwareTranslation()

const { countryOptions } = useISOCountries()

const fillFromCompany = ({ title, ident, street, city, post_code, country_code }: IntelligenceCompany) => {
  billingDetails.value = {
    ...billingDetails.value,
    companyName: title,
    companyIdent: ident,
    city: city || billingDetails.value.city,
    address: street || billingDetails.value.address,
    postNumber: post_code || billingDetails.value.postNumber,
    country: country_code,
  }
}
</script>

<template>
  <form class="grid">
    <DTField
      class="col-span-full"
      :label="t('country')"
    >
      <DTFieldSelect
        v-model="billingDetails.country"
        :options="countryOptions"
        :disabled
      />
    </DTField>
    <DTField :label="t('company-name')">
      <CompanyNameInput
        v-model="billingDetails.companyName"
        :disabled
        class="[&_fieldset]:min-w-0 [&_input]:min-w-0"
        :required="required?.companyName"
        @company-selected="fillFromCompany"
      />
    </DTField>
    <DTField :label="translateForCountry('company_ident')">
      <DTFieldInput
        v-model="billingDetails.companyIdent"
        wrapper-class="min-w-0"
        class="min-w-0"
        :required="required?.companyIdent"
        :disabled
      />
    </DTField>
    <DTField :label="t('first-name')">
      <DTFieldInput
        v-model="billingDetails.firstName"
        wrapper-class="min-w-0"
        class="min-w-0"
        :required="required?.firstName"
        :disabled
      />
    </DTField>
    <DTField :label="t('last-name')">
      <DTFieldInput
        v-model="billingDetails.lastName"
        wrapper-class="min-w-0"
        class="min-w-0"
        :required="required?.lastName"
        :disabled
      />
    </DTField>
    <DTField
      class="col-span-full"
      :label="t('address')"
    >
      <DTFieldInput
        v-model="billingDetails.address"
        wrapper-class="min-w-0"
        class="min-w-0"
        :required="required?.address"
        :disabled
      />
    </DTField>
    <DTField :label="t('post-number')">
      <DTFieldInput
        v-model="billingDetails.postNumber"
        wrapper-class="min-w-0"
        class="min-w-0"
        :required="required?.postNumber"
        :disabled
      />
    </DTField>
    <DTField :label="t('city')">
      <DTFieldInput
        v-model="billingDetails.city"
        wrapper-class="min-w-0"
        class="min-w-0"
        :required="required?.city"
        :disabled
      />
    </DTField>
    <DTField
      :label="t('email')"
    >
      <DTFieldInput
        v-model="billingDetails.email"
        wrapper-class="min-w-0"
        class="min-w-0"
        type="email"
        :required="required?.email"
        :disabled
      />
    </DTField>

    <DTField
      :label="t('phone.label')"
      :description="t('phone.hint')"
    >
      <DTFieldInput
        v-model="billingDetails.phone"
        wrapper-class="min-w-0"
        class="min-w-0"
        type="tel"
        :required="required?.phone"
        :disabled
      />
    </DTField>
  </form>
</template>
